import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

import { DS_ICONS } from 'app/shared/components/icons/ds-icons.constants';
import { NavTab, NavTabComponent } from 'app/shared/components/nav-tab/nav-tab.component';
import { PopoverComponent } from 'app/shared/components/popover/popover.component';

import { useNotificationCenter } from '../../composables/use-notification-center';
import { NotificationView } from '../../models/notification-view';
import { NotificationCenterPage, NotificationTab } from '../../pages/notification-center/notification-center.page';
import { NotificationActionService } from '../../services/notification-action.service';

@Component({
    selector: 'app-notification-popover',
    templateUrl: './notification-popover.container.html',
    styleUrl: './notification-popover.container.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:keydown.escape)': 'closeOnEscape($event)',
    },
    imports: [NavTabComponent, NotificationCenterPage, PopoverComponent],
})
export class NotificationPopoverContainer {
    private readonly translate = inject(TranslateService);
    private readonly langChange = toSignal(this.translate.onLangChange);
    private readonly popover = viewChild(PopoverComponent);
    private readonly trigger = viewChild<unknown, ElementRef<HTMLElement>>('trigger', { read: ElementRef });

    protected readonly notifications = useNotificationCenter();
    protected readonly notificationAction = inject(NotificationActionService);
    protected readonly activeTab = signal<NotificationTab>('unread');

    readonly active = input(false);
    readonly clicked = output<void>();

    protected readonly triggerTabs = computed<NavTab[]>(() => {
        this.langChange();

        return [
            {
                icon: DS_ICONS.notification,
                dot: this.notifications.hasUnread(),
                active: this.active(),
                ariaLabel: this.translate.instant('NOTIFICATION_CENTER_TITLE'),
                ariaControls: 'notification-popover-content',
                ariaExpanded: this.popover()?.isOpen() ?? false,
                ariaHasPopup: 'dialog',
            },
        ];
    });

    protected toggle(popover: PopoverComponent): void {
        this.clicked.emit();

        if (this.active()) {
            popover.close();
            return;
        }

        if (!popover.isOpen()) this.notifications.reload();
        popover.toggle();
    }

    protected open(notification: NotificationView, popover: PopoverComponent): void {
        popover.close();
        this.notificationAction.handle(notification, this.activeTab());
    }

    protected closeOnEscape(event: Event): void {
        const popover = this.popover();
        if (!popover?.isOpen()) return;

        event.preventDefault();
        popover.close();
        this.trigger()?.nativeElement.querySelector('button')?.focus();
    }
}
